import React, { useEffect, useState } from 'react';
import { useNavigation } from '../contexts/NavigationContext';
import { useAppointments } from '../contexts/AppointmentContext';
import { useWindowSize } from '../hooks';

const AdminHeader = ({ 
  setNewAppointmentModalOpen, 
  handleLogout 
}) => {
  // Usar o hook de navegação para acessar o contexto
  const { activeTab } = useNavigation();
  // Usar o hook de agendamentos para acessar os agendamentos
  const { appointments } = useAppointments();
  // Detectar se é dispositivo móvel
  const { width } = useWindowSize();
  const isMobile = width < 768; // Considera dispositivos com menos de 768px como mobile
  
  // Estado para armazenar o tamanho do safe area top
  const [safeAreaTop, setSafeAreaTop] = useState(0);
  
  // Detectar o safe area inset para iOS
  useEffect(() => {
    // Se não estiver em mobile, não precisamos executar este efeito
    if (!isMobile) return;
    
    // Função para detectar e calcular o safe area top
    const detectSafeArea = () => {
      // Para iOS com notch, podemos usar env() se disponível
      if (CSS.supports('padding-top: env(safe-area-inset-top)')) {
        const computedStyle = getComputedStyle(document.documentElement);
        const safeTop = computedStyle.getPropertyValue('--sat') || '0px';
        const safeTopValue = parseInt(safeTop, 10);
        setSafeAreaTop(isNaN(safeTopValue) ? 0 : safeTopValue);
      } else {
        // Fallback - detectar tamanho aproximado baseado na altura da janela
        // Dispositivos com notch normalmente têm safe area top de pelo menos 44px
        const isNotchLikelyPresent = 
          (window.screen.height >= 812 && window.devicePixelRatio >= 2) || // iPhone X ou superior
          (navigator.userAgent.includes('iPhone') && window.innerHeight > 800);
        
        setSafeAreaTop(isNotchLikelyPresent ? 44 : 20);
      }
    };
    
    // Adicionamos CSS variables para usar o env()
    document.documentElement.style.setProperty('--sat', 'env(safe-area-inset-top)');
    
    detectSafeArea();
    window.addEventListener('resize', detectSafeArea);
    
    return () => window.removeEventListener('resize', detectSafeArea);
  }, [isMobile]);
  
  // Calcular agendamentos pendentes
  const pendingAppointments = appointments ? appointments.filter(app => 
    !app.confirmado && !app.cancelado
  ) : [];
  
  // Função para determinar o título baseado na aba ativa
  const getTitle = () => {
    switch (activeTab) {
      case 'appointments': return 'Agendamentos';
      case 'services': return 'Serviços';
      case 'professionals': return 'Profissionais';
      case 'commission': return 'Comissões';
      case 'confirmations': return 'Confirmações';
      case 'performance': return 'Desempenho';
      case 'config': return 'Configurações';
      default: return 'Painel Administrativo';
    }
  };

  // Estilo para adicionar padding ao corpo quando é mobile, para compensar o header fixo
  useEffect(() => {
    if (isMobile) {
      // Se estiver na página de desempenho, não adicionar padding
      if (activeTab === 'performance') {
        document.body.style.paddingTop = '0';
      } else {
        // Altura base do header + safe area
        const headerHeight = 56 + safeAreaTop; // 56px é aproximadamente o tamanho do header
        document.body.style.paddingTop = `${headerHeight}px`;
      }
    } else {
      document.body.style.paddingTop = '0';
    }
    
    return () => {
      document.body.style.paddingTop = '0';
    };
  }, [isMobile, safeAreaTop, activeTab]);
  
  // Se não estiver em dispositivo móvel ou se estiver na página de desempenho, não renderizar o header
  if (!isMobile || activeTab === 'performance') {
    return null;
  }

  return (
    <header 
      className="fixed inset-x-0 top-0 z-50 bg-white border-b border-gray-200 md:hidden"
      style={{ paddingTop: `max(env(safe-area-inset-top), ${safeAreaTop}px)` }}
    >
      <div className="flex justify-between items-center px-4 py-3">
        <div className="flex items-center space-x-3">
          <h1 className="text-xl font-semibold">{getTitle()}</h1>
        </div>
      </div>
    </header>
  );
};

export default AdminHeader; 